import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, XCircle, CheckCircle, FileText, Check,
} from 'lucide-react';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * B2BConfirmOrderPage — token-based B2B order confirmation page.
 * Route: /b2b/confirm/:token
 *
 * Extracted from App.js (Fase 1) — pure relocation, no logic changes.
 */
export const B2BConfirmOrderPage = () => {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/b2b/confirm-order/${token}`);
        setOrder(res.data);
        if (res.data.already_confirmed) setConfirmed(true);
      } catch (e) {
        setError(e.response?.data?.detail || 'Ongeldige of verlopen bevestigingslink');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/b2b/confirm-order/${token}`);
      setConfirmed(true);
      setInvoiceNumber(res.data.invoice_number);
    } catch (e) {
      setError(e.response?.data?.detail || 'Bevestiging mislukt');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#081D33]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00A9E0]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#081D33] p-4">
      <Card className="max-w-md w-full p-8 text-center bg-[#0a2540] border-red-500/30">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Bevestiging Niet Mogelijk</h2>
        <p className="text-slate-400">{error}</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#081D33] p-4">
      <Card className="max-w-lg w-full p-8 bg-[#0a2540] border-slate-700">
        {confirmed ? (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Order Bevestigd!</h2>
            {invoiceNumber && <p className="text-[#00A9E0] font-medium mb-2">Factuurnummer: {invoiceNumber}</p>}
            <p className="text-slate-400 mb-6">Uw factuur is per email verzonden. Na betaling ontvangt u uw pass codes.</p>
            <Button onClick={() => window.location.href = 'https://freestays.eu'} className="rounded-full px-8">
              Naar Freestays
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <FileText className="w-12 h-12 text-[#00A9E0] mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-white">Bevestig Uw Bestelling</h2>
              <p className="text-slate-400 mt-1">{order?.business_name}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Product</span>
                <span className="text-white font-medium">{order?.pass_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Aantal</span>
                <span className="text-white font-medium">{order?.quantity}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Stukprijs (excl. BTW)</span>
                <span className="text-white font-medium">€{order?.unit_price?.toFixed(2)}</span>
              </div>
              <Separator className="bg-slate-700" />
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Subtotaal</span>
                <span className="text-white">€{order?.total_price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">BTW 21%</span>
                <span className="text-white">€{order?.btw_amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold pt-1 border-t border-slate-700">
                <span className="text-white">Totaal incl. BTW</span>
                <span className="text-[#00A9E0]">€{order?.total_price_incl_btw?.toFixed(2)}</span>
              </div>
            </div>
            <Button onClick={handleConfirm} disabled={confirming} className="w-full rounded-full h-12 bg-green-600 hover:bg-green-700 text-lg" data-testid="confirm-order-btn">
              {confirming ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Check className="w-5 h-5 mr-2" />}
              Bevestig Order
            </Button>
            <p className="text-center text-xs text-slate-500 mt-3">Na bevestiging ontvangt u automatisch de factuur per email.</p>
          </>
        )}
      </Card>
    </div>
  );
};

export default B2BConfirmOrderPage;
